// Admin-only edit screen for an existing visit. Reuses the same fields as
// VisitNew (provider picker, time, lead, ride, instructions, notes) but
// pre-fills from the loaded visit and sends a PATCH instead of POST.

import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AppShell } from "../components/AppShell";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Label } from "../components/ui/Label";
import { Textarea } from "../components/ui/Textarea";
import { Select } from "../components/ui/Select";
import { Card, CardBody } from "../components/ui/Card";
import { Sheet } from "../components/ui/Sheet";
import { api, ApiException } from "../lib/api";
import { ctLocalToUtc, fmtTimeCt, arriveByIso, utcToCtLocal } from "../lib/dates";
import { cn } from "../lib/cn";

interface Provider {
  id: string;
  name: string;
  specialty: string | null;
  facility_name: string | null;
  address: string | null;
  phone: string | null;
}

interface VisitDetailResp {
  visit: {
    id: string;
    circle_id: string;
    title: string | null;
    provider_id: string | null;
    provider_name_snapshot: string | null;
    provider_specialty_snapshot: string | null;
    facility_name_snapshot: string | null;
    address_snapshot: string | null;
    phone_snapshot: string | null;
    visit_category: string | null;
    visit_type: "in_person" | "telehealth";
    starts_at: string;
    ends_at: string | null;
    arrive_lead_minutes: number;
    estimated_travel_minutes: number | null;
    ride_required: number;
    pre_visit_instructions: string | null;
    notes: string | null;
    status: "open" | "accepted" | "cancelled" | "completed";
  };
  role: "admin" | "member";
}

const CATEGORIES = [
  ["office_visit", "Office visit"],
  ["follow_up", "Follow-up"],
  ["new_patient", "New patient"],
  ["procedure", "Procedure"],
  ["telehealth", "Telehealth"],
  ["lab", "Lab"],
  ["imaging", "Imaging"],
  ["other", "Other"],
] as const;

const LEAD_OPTIONS = [0, 15, 30, 45];

export function VisitEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [providers, setProviders] = useState<Provider[]>([]);
  const [providerSheetOpen, setProviderSheetOpen] = useState(false);
  const [newProviderName, setNewProviderName] = useState("");
  const [newProviderForm, setNewProviderForm] = useState({ specialty: "", facility_name: "", address: "", phone: "" });

  const [circleId, setCircleId] = useState("");
  const [selectedProviderId, setSelectedProviderId] = useState<string | "">("");
  const [providerName, setProviderName] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [facility, setFacility] = useState("");
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [category, setCategory] = useState<string>("office_visit");
  const [visitType, setVisitType] = useState<"in_person" | "telehealth">("in_person");
  const [meetingLink, setMeetingLink] = useState("");
  const [startsLocal, setStartsLocal] = useState("");
  const [duration, setDuration] = useState(60);
  const [leadMinutes, setLeadMinutes] = useState(15);
  const [customLead, setCustomLead] = useState("");
  const [rideRequired, setRideRequired] = useState(false);
  const [travelMin, setTravelMin] = useState<string>("");
  const [preInstructions, setPreInstructions] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const v = await api.get<VisitDetailResp>(`/api/visits/${id}`);
        if (v.role !== "admin") {
          navigate(`/app/visits/${id}`, { replace: true });
          return;
        }
        setCircleId(v.visit.circle_id);
        setSelectedProviderId(v.visit.provider_id ?? "");
        setProviderName(v.visit.provider_name_snapshot ?? "");
        setSpecialty(v.visit.provider_specialty_snapshot ?? "");
        setFacility(v.visit.facility_name_snapshot ?? "");
        const isTelehealth = v.visit.visit_type === "telehealth";
        if (isTelehealth) setMeetingLink(v.visit.address_snapshot ?? "");
        else setAddress(v.visit.address_snapshot ?? "");
        setPhone(v.visit.phone_snapshot ?? "");
        setCategory(v.visit.visit_category ?? "office_visit");
        setVisitType(v.visit.visit_type);
        setStartsLocal(utcToCtLocal(v.visit.starts_at));
        if (v.visit.ends_at) {
          const ms = new Date(v.visit.ends_at).getTime() - new Date(v.visit.starts_at).getTime();
          setDuration(Math.max(5, Math.round(ms / 60_000)));
        }
        const lead = v.visit.arrive_lead_minutes;
        if (LEAD_OPTIONS.includes(lead as 0 | 15 | 30 | 45)) {
          setLeadMinutes(lead);
          setCustomLead("");
        } else {
          setCustomLead(String(lead));
        }
        setRideRequired(!!v.visit.ride_required);
        setTravelMin(v.visit.estimated_travel_minutes != null ? String(v.visit.estimated_travel_minutes) : "");
        setPreInstructions(v.visit.pre_visit_instructions ?? "");
        setNotes(v.visit.notes ?? "");

        const p = await api.get<{ providers: Provider[] }>(`/api/circles/${v.visit.circle_id}/providers`);
        setProviders(p.providers);
      } catch (err) {
        if (err instanceof ApiException && err.status === 401) navigate("/signin", { replace: true });
        if (err instanceof ApiException && err.status === 404) setError("Visit not found.");
        if (err instanceof ApiException && err.status === 403) setError("Only admins can edit visits.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  function applyProvider(p: Provider | null) {
    setSelectedProviderId(p?.id ?? "");
    setProviderName(p?.name ?? "");
    setSpecialty(p?.specialty ?? "");
    setFacility(p?.facility_name ?? "");
    setAddress(p?.address ?? "");
    setPhone(p?.phone ?? "");
  }

  async function createProvider(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    try {
      const { id: pid } = await api.post<{ id: string }>(`/api/circles/${circleId}/providers`, {
        name: newProviderName,
        specialty: newProviderForm.specialty || null,
        facility_name: newProviderForm.facility_name || null,
        address: newProviderForm.address || null,
        phone: newProviderForm.phone || null,
      });
      const fresh: Provider = {
        id: pid,
        name: newProviderName,
        specialty: newProviderForm.specialty || null,
        facility_name: newProviderForm.facility_name || null,
        address: newProviderForm.address || null,
        phone: newProviderForm.phone || null,
      };
      setProviders([fresh, ...providers]);
      applyProvider(fresh);
      setProviderSheetOpen(false);
      setNewProviderName("");
      setNewProviderForm({ specialty: "", facility_name: "", address: "", phone: "" });
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Failed.");
    } finally {
      setBusy(false);
    }
  }

  const effectiveLead = customLead.length > 0 ? Math.max(0, parseInt(customLead) || 0) : leadMinutes;
  const startsAtUtc = startsLocal ? ctLocalToUtc(startsLocal) : "";
  const endsAtUtc = startsAtUtc ? new Date(new Date(startsAtUtc).getTime() + duration * 60_000).toISOString() : "";

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!id) return;
    setBusy(true);
    setError(null);
    try {
      const payload = {
        provider_id: selectedProviderId || null,
        provider_name: providerName || null,
        provider_specialty: specialty || null,
        facility_name: facility || null,
        address: visitType === "telehealth" ? meetingLink || null : address || null,
        phone: phone || null,
        visit_category: category,
        visit_type: visitType,
        starts_at: startsAtUtc,
        ends_at: endsAtUtc || null,
        arrive_lead_minutes: effectiveLead,
        ride_required: rideRequired,
        estimated_travel_minutes: travelMin ? parseInt(travelMin, 10) : null,
        pre_visit_instructions: preInstructions || null,
        notes: notes || null,
      };
      await api.post(`/api/visits/${id}`, payload);
      navigate(`/app/visits/${id}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiException ? err.message : "Save failed.");
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <AppShell activeTab="visits">
        <div className="p-5 text-text-muted">Loading…</div>
      </AppShell>
    );
  }

  if (error && !providerName) {
    return (
      <AppShell activeTab="visits">
        <div className="mx-auto max-w-2xl px-5 py-8">
          <div className="rounded-md border border-danger/30 bg-red-50 text-danger text-sm px-3 py-2">{error}</div>
          <div className="mt-4">
            <Link to={`/app/visits/${id}`}><Button variant="secondary">← Back</Button></Link>
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell activeTab="visits">
      <div
        className="mx-auto max-w-2xl px-5 py-6 sm:py-8"
        style={{ paddingBottom: "calc(11rem + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">Edit visit</h1>
          <Link to={`/app/visits/${id}`} className="text-sm text-text-muted hover:text-text">Cancel</Link>
        </div>

        <form onSubmit={submit} className="mt-5 space-y-5" noValidate>
          {/* PROVIDER */}
          <Card>
            <CardBody>
              <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">Provider</h2>
              <div className="mt-3">
                <Label htmlFor="prov">Pick a provider</Label>
                <Select
                  id="prov"
                  value={selectedProviderId}
                  onChange={(e) => {
                    const p = providers.find((x) => x.id === e.target.value);
                    applyProvider(p ?? null);
                  }}
                >
                  <option value="">— Custom (no provider) —</option>
                  {providers.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.specialty ? ` · ${p.specialty}` : ""}
                    </option>
                  ))}
                </Select>
                <button type="button" onClick={() => setProviderSheetOpen(true)} className="mt-2 text-sm text-primary underline">
                  + Add new provider
                </button>
              </div>

              <div className="mt-4 space-y-3">
                <div>
                  <Label htmlFor="pname">Doctor</Label>
                  <Input id="pname" value={providerName} onChange={(e) => setProviderName(e.target.value)} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="spec">Specialty</Label>
                    <Input id="spec" value={specialty} onChange={(e) => setSpecialty(e.target.value)} />
                  </div>
                  <div>
                    <Label htmlFor="fac">Facility</Label>
                    <Input id="fac" value={facility} onChange={(e) => setFacility(e.target.value)} />
                  </div>
                </div>
                {visitType === "in_person" && (
                  <div>
                    <Label htmlFor="addr">Address</Label>
                    <Textarea id="addr" rows={2} value={address} onChange={(e) => setAddress(e.target.value)} />
                  </div>
                )}
                <div>
                  <Label htmlFor="ph">Phone</Label>
                  <Input id="ph" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
              </div>
            </CardBody>
          </Card>

          {/* WHEN */}
          <Card>
            <CardBody>
              <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">When</h2>
              <div className="mt-3 space-y-3">
                <div>
                  <Label htmlFor="starts">Start time (CT)</Label>
                  <Input id="starts" type="datetime-local" required value={startsLocal} onChange={(e) => setStartsLocal(e.target.value)} />
                </div>
                <div>
                  <Label>Arrive how early?</Label>
                  <div className="flex gap-2 flex-wrap">
                    {LEAD_OPTIONS.map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => { setLeadMinutes(m); setCustomLead(""); }}
                        className={cn(
                          "px-3 py-2 rounded-md border text-sm font-medium",
                          customLead === "" && leadMinutes === m
                            ? "bg-primary text-primary-fg border-primary"
                            : "bg-surface text-text border-border hover:bg-surface-2",
                        )}
                      >
                        {m} min
                      </button>
                    ))}
                    <Input
                      type="number" min={0} max={240} placeholder="Custom"
                      value={customLead} onChange={(e) => setCustomLead(e.target.value)}
                      className="w-24"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="dur">Duration (minutes)</Label>
                  <Input
                    id="dur" type="number" min={5} max={600}
                    value={duration}
                    onChange={(e) => setDuration(Math.max(5, parseInt(e.target.value, 10) || 60))}
                  />
                </div>
                {startsLocal && (
                  <div className="rounded-md bg-surface-2 p-3 text-sm">
                    <div className="font-medium">Arrive by {fmtTimeCt(arriveByIso(startsAtUtc, effectiveLead))}</div>
                    <div className="text-text-muted">Starts {fmtTimeCt(startsAtUtc)} · ~{duration} min</div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* LOGISTICS */}
          <Card>
            <CardBody>
              <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">Logistics</h2>
              <div className="mt-3 space-y-3">
                <div>
                  <Label htmlFor="cat">Visit category</Label>
                  <Select id="cat" value={category} onChange={(e) => setCategory(e.target.value)}>
                    {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </Select>
                </div>
                <div>
                  <Label>Visit type</Label>
                  <div className="flex gap-2">
                    {(["in_person", "telehealth"] as const).map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setVisitType(t)}
                        className={cn(
                          "flex-1 px-3 py-2 rounded-md border text-sm font-medium",
                          visitType === t ? "bg-primary text-primary-fg border-primary" : "bg-surface text-text border-border",
                        )}
                      >
                        {t === "in_person" ? "In person" : "Telehealth"}
                      </button>
                    ))}
                  </div>
                </div>
                {visitType === "telehealth" && (
                  <div>
                    <Label htmlFor="link">Meeting link</Label>
                    <Input id="link" type="url" value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} />
                  </div>
                )}
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={rideRequired} onChange={(e) => setRideRequired(e.target.checked)} className="h-4 w-4" />
                  <span className="text-sm">Ride required</span>
                </label>
                {rideRequired && (
                  <div>
                    <Label htmlFor="travel">Estimated travel time (minutes)</Label>
                    <Input id="travel" type="number" min={0} max={600} value={travelMin} onChange={(e) => setTravelMin(e.target.value)} />
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          {/* INSTRUCTIONS */}
          <Card>
            <CardBody>
              <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">Instructions & notes</h2>
              <div className="mt-3 space-y-3">
                <div>
                  <Label htmlFor="instr">Pre-visit instructions</Label>
                  <Textarea id="instr" rows={3} value={preInstructions} onChange={(e) => setPreInstructions(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="ntz">Family notes</Label>
                  <Textarea id="ntz" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>
              </div>
            </CardBody>
          </Card>

          {error && (
            <div role="alert" className="rounded-md border border-danger/30 bg-red-50 text-danger text-sm px-3 py-2">{error}</div>
          )}
        </form>

        {/* Sticky save bar */}
        <div
          className="fixed bottom-0 sm:left-64 inset-x-0 bg-surface border-t border-border p-3 sm:p-4 z-20"
          style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        >
          <div className="mx-auto max-w-2xl flex gap-3">
            <Button onClick={(e) => submit(e as unknown as FormEvent<HTMLFormElement>)} loading={busy} size="lg" className="flex-1">
              Save changes
            </Button>
            <Link to={`/app/visits/${id}`}>
              <Button variant="ghost" size="lg" type="button">Cancel</Button>
            </Link>
          </div>
        </div>

        <Sheet
          open={providerSheetOpen}
          onClose={() => setProviderSheetOpen(false)}
          title="Add provider"
        >
          <form onSubmit={createProvider} className="space-y-4">
            <div>
              <Label htmlFor="np-name">Name</Label>
              <Input id="np-name" required value={newProviderName} onChange={(e) => setNewProviderName(e.target.value)} placeholder="Dr. N Vallala" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="np-spec">Specialty</Label>
                <Input id="np-spec" value={newProviderForm.specialty} onChange={(e) => setNewProviderForm({ ...newProviderForm, specialty: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="np-fac">Facility</Label>
                <Input id="np-fac" value={newProviderForm.facility_name} onChange={(e) => setNewProviderForm({ ...newProviderForm, facility_name: e.target.value })} />
              </div>
            </div>
            <div>
              <Label htmlFor="np-addr">Address</Label>
              <Textarea id="np-addr" rows={2} value={newProviderForm.address} onChange={(e) => setNewProviderForm({ ...newProviderForm, address: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="np-ph">Phone</Label>
              <Input id="np-ph" type="tel" value={newProviderForm.phone} onChange={(e) => setNewProviderForm({ ...newProviderForm, phone: e.target.value })} />
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="submit" loading={busy}>Save provider</Button>
              <Button type="button" variant="ghost" onClick={() => setProviderSheetOpen(false)}>Cancel</Button>
            </div>
          </form>
        </Sheet>
      </div>
    </AppShell>
  );
}
